import { FieldArray, FormikProps } from "formik";
import { useEffect, useState } from "react";
import { GoPlus } from "react-icons/go";
import { MdDelete } from "react-icons/md";
import { RxCross2 } from "react-icons/rx";
import { useLocation, useParams } from "react-router-dom";
import ATMLoadingButton from "src/components/UI/atoms/ATMLoadingButton/ATMLoadingButton";
import ATMTable, {
  columnTypes,
} from "src/components/UI/atoms/ATMTable/ATMTable";
import ATMDatePicker from "src/components/UI/atoms/formFields/ATMDatePicker/ATMDatePicker";
import ATMSelect from "src/components/UI/atoms/formFields/ATMSelect/ATMSelect";
import ATMTextArea from "src/components/UI/atoms/formFields/ATMTextArea/ATMTextArea";
import ATMTextField from "src/components/UI/atoms/formFields/ATMTextField/ATMTextField";
import useAccounts from "src/hooks/useAccounts";
import useBatches from "src/hooks/useBatches";
import { AssignBatchesValues } from "src/models/Student.model";
import { useAssignBatchesStudentByIdQuery } from "src/services/StudentServices";
import { paymentModeOptions } from "src/utils/paymentModeOptions";
import { handleValidNumber } from "src/utils/validations/numberInput";

type Props = {
  formikProps: FormikProps<AssignBatchesValues>;
  onCouponClick: () => void;
  couponData: any;
};

const AssignBatches = ({ formikProps, onCouponClick, couponData }: Props) => {
  const { values, setFieldValue, handleBlur, errors, touched }: any =
    formikProps;
  const { state } = useLocation();
  const { studentId } = useParams();
  const { batches, isDataLoading } = useBatches();
  const { accounts, isDataLoading: isAccountsLoading } = useAccounts();
  const accountsOptions = accounts?.map((account) => {
    return {
      label: account?.accountName,
      value: account?._id,
    };
  });

  const [assginBatchData, setAssginBatchData] = useState([]);
  const { data, isLoading, isFetching } =
    useAssignBatchesStudentByIdQuery(studentId);

  useEffect(() => {
    if (!isLoading && !isFetching) {
      setAssginBatchData(data?.data?.batches);
    }
  }, [data, isLoading, isFetching]);

  const handleBatchInfo = (batchId: string) => {
    let batchInfo = batches?.find((batch) => batch?._id === batchId);
    setFieldValue("batch", batchInfo);
    setFieldValue("totalFee", batchInfo?.fees);
  };
  //remove coupon function
  const handleRemoveCoupon = () => {
    setFieldValue("couponCode", "");
    setFieldValue("couponDiscount", "");
  };

  const columns: columnTypes[] = [
    {
      field: "batchName",
      headerName: "Batch Name",
      flex: "flex-[1_1_0%]",
      extraClasses: "min-w-[100px] py-2",
    },
    {
      field: "startDate",
      headerName: "Start Date",
      flex: "flex-[1.75_1.75_0%]",
      extraClasses: "min-w-[200px] py-2",
    },
    {
      field: "time",
      headerName: "Timing",
      flex: "flex-[2_2_0%]",
      extraClasses: "min-w-[160px] flex-wrap px-2",
    },
    {
      field: "fees",
      headerName: "Fees",
      flex: "flex-[0.5_0.5_0%]",
      extraClasses: "min-w-[150px]",
    },
  ];

  //setting coupon info
  useEffect(() => {
    setFieldValue("couponCode", couponData?.couponCode);
    let calculatedDiscount: any =
      couponData?.amountType === "PERCENTAGE"
        ? (Number(values?.totalFee || 0) * couponData?.discountAmount) / 100
        : couponData?.discountAmount;

    let finalDiscountAmount =
      couponData?.amountType === "PERCENTAGE"
        ? Number(calculatedDiscount || 0) <
          Number(couponData?.maxDiscountAmount || 0)
          ? calculatedDiscount
          : couponData?.maxDiscountAmount
        : calculatedDiscount;
    setFieldValue(
      "couponDiscount",
      Number(finalDiscountAmount || 0) > Number(values?.totalFee || 0)
        ? values?.totalFee
        : finalDiscountAmount
    );
    setFieldValue("discountType", couponData?.amountType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [couponData]);

  //setting payable amount
  useEffect(() => {
    let payableAmount =
      Number(values?.totalFee || 0) - Number(values?.couponDiscount || 0) || 0;
    setFieldValue("payableAmount", payableAmount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values?.totalFee, values?.couponDiscount]);

  //setting due amount
  useEffect(() => {
    let dueAmount =
      Number(values?.payableAmount || 0) -
        Number(values?.receivedAmount || 0) || 0;
    setFieldValue("dueAmount", dueAmount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values?.payableAmount, values?.receivedAmount]);

  useEffect(() => {
    setFieldValue("installment[0].amount", values?.dueAmount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values?.dueAmount]);

  //get remaining installment amount
  const getRemainingAmount = () => {
    const totalInstallmentAmount = values?.installment?.reduce(
      (acc: any, el: any) => {
        acc += Number(el?.amount || 0);
        return acc;
      },
      0
    );
    const remainingInstallmentAmt =
      Number(values?.dueAmount || 0) - totalInstallmentAmount;
    return remainingInstallmentAmt > 0 ? remainingInstallmentAmt : 0;
  };

  // ASSIGN BATCHS AND NOT ASSIGN BATCHS OPTIONS

  const batchOptions = batches?.map((el: any) => {
    return {
      batchName: el?.batchName,
      _id: el?._id,
    };
  });

  const assginBatchOptions = assginBatchData?.map((el: any) => {
    return {
      batchName: el?.name,
      _id: el?.batchId,
    };
  });

  const notAssginBatchs = batchOptions?.filter(
    (item1) =>
      assginBatchOptions?.findIndex(
        (item2: any) => item1?._id === item2?._id
      ) === -1
  );

  return (
    <div className="flex flex-col gap-4 p-3">
      <div className="flex items-end gap-5">
        {/* Batch */}
        <div className="px-4 py-2 rounded-lg bg-slate-200 ">
          <span className="font-semibold text-gray-900 ">Name :</span>{" "}
          {state?.studenName}
        </div>
        <div>
          <ATMSelect
            name="batch"
            value={values.batch}
            onChange={(value) => {
              setFieldValue("batch", value);
              handleBatchInfo(value?._id);
            }}
            label="Batches"
            onBlur={handleBlur}
            isLoading={isDataLoading}
            options={notAssginBatchs}
            renderOption={(option, { context }) => {
              if (context === "menu") {
                return <div>{option?.batchName}</div>;
              } else {
                return <div>{option?.batchName}</div>;
              }
            }}
            isOptionEqualToValue={(option, selected) =>
              selected?.[0]?._id === option?._id
            }
            isSearchedOption={(option, searchValue) =>
              searchValue
                ? option?.batchName?.toLowerCase()?.includes(searchValue)
                : true
            }
            menuPosition="absolute"
          />
        </div>
      </div>

      {values?.batch && Object.values(values?.batch)?.length ? (
        <>
          {/* Selected Batch */}
          <div className="w-full overflow-auto">
            <p className="text-sm font-medium text-slate-700">Selected Batch</p>
            <ATMTable
              columns={columns}
              rows={[values?.batch]}
              rowExtraClasses={() => "min-h-[40px]"}
            />
          </div>
          {/* Payment Summary */}
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
            <div className="p-3 mt-2 border rounded">
              <h6 className="font-medium text-slate-700 ">Payment Summary</h6>

              <div className="flex justify-between mt-3">
                <p>Total Fees</p>
                <p className="font-semibold">&#8377;{values?.totalFee || 0}</p>
              </div>
              <div className="flex justify-between mt-3">
                <div>
                  <p>Coupon</p>
                  {values?.couponCode && (
                    <div className="flex items-center gap-2 px-2 py-1 rounded-lg bg-amber-400 text-dark">
                      <span className="text-xs">{values?.couponCode}</span>
                      <RxCross2
                        size={15}
                        className="cursor-pointer"
                        onClick={() => {
                          handleRemoveCoupon();
                        }}
                      />
                    </div>
                  )}
                </div>
                {values?.couponCode ? (
                  <p className="font-semibold text-green-500">
                    {" "}
                    - &#8377;{values?.couponDiscount}
                  </p>
                ) : (
                  <button
                    className="flex items-center justify-center gap-2 px-2 py-1 text-xs text-white uppercase border rounded bg-primary-main"
                    onClick={onCouponClick}
                    type="button"
                  >
                    Apply
                  </button>
                )}
              </div>

              <div className="flex justify-between mt-3">
                <p>Payable Amount</p>
                <p className="font-semibold">&#8377;{values?.payableAmount}</p>
              </div>
              <div className="flex justify-between mt-3">
                <p>Payment Mode</p>
                {/* Payment Mode */}
                <div className="">
                  <ATMSelect
                    name="mode"
                    value={values?.mode}
                    onChange={(newValue) => setFieldValue("mode", newValue)}
                    onBlur={handleBlur}
                    placeholder="Select Payment Mode"
                    options={paymentModeOptions}
                  />
                </div>
              </div>

              <div className="flex justify-between mt-5">
                <p>Account</p>
                {/* Account */}
                <div className="">
                  <ATMSelect
                    name="accountId"
                    value={values?.accountId}
                    onChange={(newValue) =>
                      setFieldValue("accountId", newValue)
                    }
                    onBlur={handleBlur}
                    placeholder="Select Account"
                    options={accountsOptions}
                    isLoading={isAccountsLoading}
                  />
                </div>
              </div>
              <div className="flex justify-between mt-5">
                <p>Received Amount</p>
                <div className="">
                  <ATMTextField
                    name="receivedAmount"
                    value={values?.receivedAmount}
                    onChange={(e) =>
                      handleValidNumber(e) &&
                      setFieldValue("receivedAmount", e.target.value)
                    }
                    label=""
                    onBlur={handleBlur}
                    className="text-end"
                  />
                </div>
              </div>
              <div className="flex justify-between mt-3">
                <p>Due Amount</p>
                <p className="font-semibold">&#8377;{values?.dueAmount}</p>
              </div>
            </div>
            {/* Installment */}
            {values?.dueAmount ? (
              <div className="p-3 mt-2 border rounded">
                {/* Payment Remark */}
                <div className="col-span-full">
                  <ATMTextArea
                    name="paymentRemark"
                    value={values?.paymentRemark}
                    onChange={(newValue) =>
                      setFieldValue("paymentRemark", newValue)
                    }
                    label="Payment Remark"
                    placeholder="Enter Payment Remark"
                    onBlur={handleBlur}
                  />
                </div>

                <h6 className="font-medium text-slate-700">Installment</h6>

                <div className="mt-1">
                  <FieldArray
                    name="installment"
                    render={({ push, remove }) => (
                      <>
                        {values?.installment?.map(
                          (installmentData: any, index: any) => {
                            return (
                              <div key={index}>
                                <div className="grid items-center grid-cols-12 gap-6 mt-3">
                                  {/* Date Picker */}
                                  <div className="col-span-5">
                                    <ATMDatePicker
                                      name={`installment[${index}].date`}
                                      value={installmentData?.date || null}
                                      onChange={(newValue) =>
                                        setFieldValue(
                                          `installment[${index}].date`,
                                          newValue
                                        )
                                      }
                                      label="Due Date"
                                    />
                                  </div>
                                  <div className="col-span-5">
                                    <ATMTextField
                                      name={`installment[${index}].amount`}
                                      value={installmentData?.amount}
                                      onChange={(e) =>
                                        handleValidNumber(e) &&
                                        setFieldValue(
                                          `installment[${index}].amount`,
                                          e.target.value
                                        )
                                      }
                                      label="Amount"
                                      placeholder="Enter Amount"
                                      onBlur={handleBlur}
                                    />
                                  </div>
                                  <div className="col-span-1 mt-3">
                                    <MdDelete
                                      color="#F70913"
                                      size={30}
                                      className="pl-1 cursor-pointer"
                                      onClick={() => remove(index)}
                                    />
                                  </div>
                                </div>
                              </div>
                            );
                          }
                        )}

                        <div className="flex justify-end mt-2">
                          <button
                            className="text-link font-semibold text-base text-[16px] max-[1024px]:text-sm flex items-center cursor-pointer"
                            disabled={
                              values?.installment?.reduce(
                                (sum: number, installmentData: any) => {
                                  return sum + Number(installmentData?.amount);
                                },
                                0
                              ) < values?.dueAmount
                                ? false
                                : true
                            }
                            onClick={() =>
                              push({
                                date: "",
                                amount: getRemainingAmount(),
                              })
                            }
                          >
                            <GoPlus
                              size={18}
                              color="#0064C4"
                              className="mr-2"
                            />
                            Add Installment
                          </button>
                        </div>
                      </>
                    )}
                  />
                  {errors?.installment &&
                  touched?.installment &&
                  !Array.isArray(errors?.installment) ? (
                    <p className="font-poppins text-[14px] text-start mt-0 text-red-500">
                      {errors?.installment}
                    </p>
                  ) : null}
                </div>
              </div>
            ) : null}
          </div>
        </>
      ) : null}
      <div className="flex justify-end ">
        <ATMLoadingButton type="submit" className="w-[100px]">
          Proceed
        </ATMLoadingButton>
      </div>
    </div>
  );
};

export default AssignBatches;
